import { Chart } from 'chart.js';
import { ChartOptions } from 'chart.js';
import { computeThresholdDate } from '../date-utils';

function cssVar(name: string): string {
  return getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
}

export class DefaultChartOptions {
  public static getVerticalLinePlugin(): any {
    return {
      id: 'verticalLine',
      afterDraw: (chart: any) => {
        try {
          const tooltipActive =
            chart &&
            chart.tooltip &&
            chart.tooltip._active &&
            chart.tooltip._active.length;
          if (!tooltipActive) return;
          const active = chart.tooltip._active[0];
          if (!active || !active.element) return;
          const x = active.element.x;
          const y = active.element.y;
          const ctx = chart.ctx;
          const top = chart.scales?.y?.top ?? 0;
          const bottom = chart.scales?.y?.bottom ?? chart.height;
          const isDarkMode =
            document.documentElement.classList.contains('my-app-dark');
          const lineColor = isDarkMode ? '#737373' : '#688bc5ff';

          ctx.save();
          ctx.beginPath();
          ctx.moveTo(x, top);
          ctx.lineTo(x, bottom);
          ctx.lineWidth = 3;
          ctx.strokeStyle = isDarkMode
            ? 'rgba(115,115,115,0.35)'
            : 'rgba(104,139,197,0.35)';
          ctx.stroke();
          ctx.beginPath();
          ctx.fillStyle = lineColor;
          ctx.arc(x, y, 4, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
        } catch (e) {}
      },
    };
  }

  public static getHealthIndicatorDataset(
    data: number[] | Array<{ x: number; y: number }>,
    periodInMinutes: number
  ): any {
    const isDarkMode =
      document.documentElement.classList.contains('my-app-dark');
    const lineColor = isDarkMode ? '#b8b8b8ff' : '#688bc5ff';

    return {
      data,
      label: 'Health',
      pointRadius: 0,
      pointHoverRadius: 6,
      pointHitRadius: 6,
      pointBackgroundColor: 'transparent',
      pointBorderColor: 'transparent',
      pointHoverBackgroundColor: lineColor,
      pointHoverBorderColor: lineColor,
      showLine: true,
      fill: false,
      borderColor: lineColor,
      borderWidth: 2,
      tension: 0.4,
      cubicInterpolationMode: 'monotone',
      spanGaps: periodInMinutes * 2 * 60 * 1000,
    };
  }

  public static getHealthIndicatorOptions(): any {
    const isDarkMode =
      document.documentElement.classList.contains('my-app-dark');
    const textColor = isDarkMode ? '#ffffffff' : '#737373';
    const now = Date.now();
    const thirtyDaysAgo = now - 30 * 24 * 60 * 60 * 1000;
    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      interaction: { mode: 'index', intersect: false },
      elements: {
        line: { tension: 0 },
        point: { radius: 0, hoverRadius: 4, hitRadius: 8 },
      },
      scales: {
        x: {
          type: 'time',
          min: thirtyDaysAgo,
          max: now,
          time: {
            unit: 'hour',
            displayFormats: {
              hour: 'dd/MM HH:mm',
            },
            tooltipFormat: 'dd/MM HH:mm',
          },
          display: false,
          grid: { display: false },
        },
        y: {
          ticks: { color: textColor, font: { size: 10 } },
          grid: { display: false, drawBorder: true },
        },
      },
      plugins: {
        legend: { display: false },
        tooltip: { enabled: true },
      },
    };
  }

  public static updateHealthIndicatorDatasetColors(chart: any): void {
    if (!chart) return;
    const isDarkMode =
      document.documentElement.classList.contains('my-app-dark');
    const lineColor = isDarkMode ? '#d3d3d3ff' : '#688bc5ff';

    chart.data.datasets.forEach((dataset: any) => {
      if (dataset.label !== 'Health') return;
      dataset.borderColor = lineColor;
      dataset.pointHoverBackgroundColor = lineColor;
      dataset.pointHoverBorderColor = lineColor;
    });
  }

  private static getTimeUnit(
    timePeriod: string
  ): 'hour' | 'day' | 'week' | 'month' {
    const validUnits = ['hour', 'day', 'week', 'month'] as const;
    return (validUnits.includes(timePeriod as any) ? timePeriod : 'day') as
      | 'hour'
      | 'day'
      | 'week'
      | 'month';
  }

  public static getOptions(
    timePeriod: string,
    dateFormat: string,
    yAxisLabel?: string,
    showLegend: boolean = true,
    selectedPeriod?: string
  ): ChartOptions<'line'> {
    const timeUnit = this.getTimeUnit(timePeriod);

    const isDarkMode =
      document.documentElement.classList.contains('my-app-dark');

    const text = isDarkMode ? cssVar('--chart-text') || '#6b7280' : '#6b7280';
    const grid = isDarkMode
      ? cssVar('--chart-grid') || 'rgba(255,255,255,0.15)'
      : 'rgba(0, 0, 0, 0.1)';
    const border = isDarkMode
      ? cssVar('--chart-border') || 'rgba(255,255,255,0.5)'
      : 'rgba(0, 0, 0, 0.1)';
    const ttBg = cssVar('--chart-tooltip-bg') || '#ffffff';
    const ttText = cssVar('--chart-tooltip-text') || '#000000';

    const now = Date.now();
    const xMin = selectedPeriod
      ? computeThresholdDate(selectedPeriod).getTime()
      : undefined;
    const xMax = selectedPeriod ? now : undefined;

    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      interaction: {
        mode: 'index',
        intersect: false,
      },
      elements: {
        line: {
          tension: 0.2,
          cubicInterpolationMode: 'monotone',
        },
        point: {
          radius: 0,
          hoverRadius: 4,
          hitRadius: 8,
        },
      },
      ...(isDarkMode && { color: text }),
      scales: {
        x: {
          type: 'time',
          ...(xMin !== undefined && { min: xMin }),
          ...(xMax !== undefined && { max: xMax }),
          time: {
            unit: timeUnit,
            displayFormats: {
              hour: 'MMM dd, HH:mm',
              day: 'MMM dd',
              week: 'MMM dd',
              month: 'MMM yyyy',
            },
            tooltipFormat: dateFormat,
          },
          grid: {
            display: true,
            color: grid,
          },
          ticks: {
            autoSkip: true,
            maxTicksLimit: 10,
            color: text,
          },
        },
        y: {
          beginAtZero: false,
          grid: {
            display: true,
            color: grid,
          },
          ticks: {
            color: text,
          },
          title: {
            display: !!yAxisLabel,
            text: yAxisLabel,
            color: text,
          },
        },
      },
      plugins: {
        legend: {
          display: showLegend,
          position: 'bottom',
          labels: {
            usePointStyle: true,
            padding: 20,
            color: text,
            generateLabels: (chart) => {
              const original =
                Chart.defaults.plugins.legend.labels.generateLabels(chart);

              return original.map((item: any) => {
                const dataset = chart.data.datasets[item.datasetIndex] as any;
                return {
                  ...item,
                  text: dataset.descriptor
                    ? `${dataset.descriptor} (${dataset.label})`
                    : dataset.label,
                };
              });
            },
          },
        },

        tooltip: {
          backgroundColor: ttBg,
          titleColor: ttText,
          bodyColor: ttText,
          borderColor: 'rgba(255, 255, 255, 0.2)',
          borderWidth: 1,
          cornerRadius: 6,
          displayColors: true,
          callbacks: {
            title: (context) => {
              if (context.length > 0) {
                return context[0].label || '';
              }
              return '';
            },
            label: (context: any) => {
              const label = context.dataset.label || '';
              const value: number = context.parsed.y;
              const unit = context.dataset?.unit || '';
              const map: Record<number, string> | undefined =
                context.dataset?.valueLabels;
              if (map && Number.isFinite(value) && map[value] !== undefined) {
                return `${label}: ${map[value]}`;
              }

              if (Number.isFinite(value)) {
                return `${label}: ${value.toFixed(2)} ${unit}`.trim();
              }
              return `${label}: ${value} ${unit}`.trim();
            },
          },
        },

        decimation: {
          enabled: true,
          algorithm: 'lttb' as const,
          samples: 500,
          threshold: 200,
        },
        zoom: {
          limits: {
            x: { min: 'original', max: 'original' },
            y: { min: 'original', max: 'original' },
          },
          pan: {
            enabled: true,
            mode: 'x',
          },
          zoom: {
            wheel: {
              enabled: true,
            },
            pinch: {
              enabled: true,
            },
            mode: 'x',
          },
        },
      },
    };
  }
}
